import { useState } from 'react'
import { talkProbe } from '@/talk/talkProbe'

type Line = { name: string; detail: string }

function audioSession(): string {
  const nav = navigator as Navigator & { audioSession?: { type?: string } }
  return nav.audioSession?.type || 'none'
}

async function permissionState(): Promise<string> {
  const nav = navigator as Navigator & {
    permissions?: { query: (desc: { name: string }) => Promise<{ state: string }> }
  }
  try {
    const status = await nav.permissions?.query({ name: 'microphone' })
    return status?.state || 'missing'
  } catch (err) {
    return err instanceof Error ? err.name : 'query-throw'
  }
}

export function MicCheckPage() {
  const [lines, setLines] = useState<Line[]>([])

  function add(name: string, detail: string) {
    talkProbe(`${name}-${detail}`)
    setLines(prev => [...prev, { name, detail }])
  }

  async function runDirect() {
    add('ctx', window.isSecureContext ? 'secure' : 'insecure')
    add('session', audioSession())
    add('perm', await permissionState())
    if (!navigator.mediaDevices?.getUserMedia) {
      add('gum', 'missing')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const track = stream.getAudioTracks()[0]
      add('gum', track ? 'ok' : 'notrack')
      stream.getTracks().forEach(t => t.stop())
    } catch (err) {
      add('gum', err instanceof Error ? (err.name || 'throw') : 'throw')
    }
  }

  async function runAfterSession() {
    const nav = navigator as Navigator & { audioSession?: { type: string } }
    try {
      if (nav.audioSession) nav.audioSession.type = 'play-and-record'
    } catch {
      add('session-set', 'throw')
    }
    add('session-now', audioSession())
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      add('gum-session', 'ok')
      stream.getTracks().forEach(t => t.stop())
    } catch (err) {
      add('gum-session', err instanceof Error ? (err.name || 'throw') : 'throw')
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <h1 className="text-xl font-semibold">Mikrofon-Test</h1>
      <p className="mt-2 text-sm text-slate-400">Ein Tipp. Danach die talk-probe-Zeilen aus dem Server-Log.</p>
      <div className="mt-6 flex gap-3">
        <button type="button" className="rounded bg-sky-600 px-4 py-3" onClick={() => { void runDirect() }}>
          Direkt
        </button>
        <button type="button" className="rounded bg-rose-700 px-4 py-3" onClick={() => { void runAfterSession() }}>
          Nach Session
        </button>
      </div>
      <ul className="mt-6 space-y-1 font-mono text-sm">
        {lines.map((line, i) => (
          <li key={`${line.name}-${i}`}>{line.name}: {line.detail}</li>
        ))}
      </ul>
    </main>
  )
}
